/**
 * Knowledgebase Hub: search the team's SOPs and documents, indexed read-only from
 * DWA's Google shared drive (KB knowledgebase.md). Google Drive stays the source of
 * truth: every result opens the file in Drive, where it's edited. Nothing is edited here.
 *
 * URL holds the search (`?q=&folder=&type=`) and the open document (`?doc=<fileId>`,
 * the viewer: KbViewer) so both can be shared or bookmarked. With no query the page
 * browses, either the overview (folders + recently updated) or "Browse all" (every
 * indexed file, sortable, 50 per page). View and sort are remembered per user in
 * browser storage (kbPrefs.ts). Admins get Sync now and Manage sources.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faChevronDown, faChevronRight, faClock, faFolder, faList, faMagnifyingGlass, faTableCellsLarge } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { GradientPage, Panel } from '../hubs/HubCards';
import { HubLayout } from '../hubs/HubHeader';
import {
  fetchKbFolders,
  fetchKbStatus,
  searchKnowledgebase,
  syncKnowledgebase,
  KB_TYPE_LABELS,
  type KbFolder,
  type KbResult,
  type KbSearchResponse,
  type KbStatusResponse,
  type KbType,
} from '../services/kbApi';
import {
  LIST_SORT_OPTIONS,
  SEARCH_SORT_OPTIONS,
  parseListSort,
  readKbPrefs,
  writeKbPrefs,
  type KbPrefs,
  type KbSearchSort,
} from './kbPrefs';
import { KbViewer } from './KbViewer';
import { GUIDE_START, GuideHits, GuideView } from './GuideView';
import { FormsSidebar, GuideSidebar, InternalFormsPanel } from './InternalForms';
import { searchGuide, type GuideHit } from '../services/kbApi';
import { initialOpenFolders, visibleFolderRows } from './kbFolderTree';

const PAGE_SIZE = 20;
const LIST_PAGE_SIZE = 50;
const DEBOUNCE_MS = 350;
const KB_TYPES = Object.keys(KB_TYPE_LABELS) as KbType[];

const card: React.CSSProperties = {
  background: DWA.cardBg,
  borderRadius: DWA.radius,
  border: '1px solid rgba(255,255,255,0.3)',
  boxShadow: DWA.cardShadow,
  color: DWA.ink,
};

const muted: React.CSSProperties = { color: DWA.textSecondary, fontSize: '0.85rem', margin: 0, lineHeight: 1.5 };

const goldButton: React.CSSProperties = {
  padding: '0.55rem 1.1rem',
  backgroundColor: DWA.gold,
  color: DWA.onGold,
  border: 'none',
  borderRadius: DWA.radius,
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: DWA.font,
  whiteSpace: 'nowrap',
};

const select: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  borderRadius: DWA.radius,
  border: '1px solid rgba(255,255,255,0.25)',
  background: 'rgba(255,255,255,0.08)',
  color: DWA.onDark,
  fontSize: '0.85rem',
  fontFamily: DWA.font,
  maxWidth: '100%',
  minWidth: 0,
};

function formatKbDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

const Snippet: React.FC<{ result: KbResult }> = ({ result }) => {
  if (!result.snippet.length) {
    return result.text_indexed ? null : (
      <p style={{ ...muted, fontStyle: 'italic' }}>Title only: this file type isn't searched inside. Open it in Drive.</p>
    );
  }
  return (
    <p style={{ ...muted, color: DWA.ink, overflowWrap: 'anywhere' }}>
      {result.snippet.map((s, i) =>
        s.match ? (
          <mark key={i} style={{ background: DWA.goldSoft, color: DWA.ink, fontWeight: 700, padding: '0 0.1em', borderRadius: 3 }}>
            {s.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{s.text}</React.Fragment>
        ),
      )}
      {result.snippet.length > 0 && '…'}
    </p>
  );
};

const titleButton: React.CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: DWA.ink,
  font: 'inherit',
  fontWeight: 'inherit',
  textAlign: 'left',
  overflowWrap: 'anywhere',
};

const ResultCard: React.FC<{ result: KbResult; onFolder: (path: string) => void; onOpen: (fileId: string) => void }> = ({
  result,
  onFolder,
  onOpen,
}) => (
  <article data-testid={`kb-result-${result.file_id}`} style={{ ...card, padding: '1rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.45rem', minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: '1 1 16rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, overflowWrap: 'anywhere' }}>
          <button type="button" onClick={() => onOpen(result.file_id)} title="View in Omega Wolf" style={titleButton}>
            {result.title}
          </button>
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: DWA.textSecondary,
              background: 'rgba(0,0,0,0.05)',
              borderRadius: 999,
              padding: '0.1rem 0.5rem',
            }}
          >
            {result.kind_label}
          </span>
          {result.folder_path && (
            <button
              type="button"
              onClick={() => onFolder(result.folder_path)}
              title={`Show ${result.folder_path}`}
              style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: DWA.textSecondary, fontSize: '0.78rem', fontFamily: DWA.font, textAlign: 'left', overflowWrap: 'anywhere' }}
            >
              <FontAwesomeIcon icon={faFolder} style={{ marginRight: '0.3rem', color: DWA.textMuted }} />
              {result.folder_path}
            </button>
          )}
        </div>
      </div>
      {result.web_view_link && (
        <a
          href={result.web_view_link}
          target="_blank"
          rel="noopener noreferrer"
          className="hub-gold-button"
          style={{ ...goldButton, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
        >
          Open in Drive
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '0.7rem' }} />
        </a>
      )}
    </div>
    <Snippet result={result} />
    {(result.modified_time || result.last_modified_by) && (
      <p style={{ ...muted, fontSize: '0.75rem', color: DWA.textMuted }}>
        Updated {formatKbDate(result.modified_time)}
        {result.last_modified_by ? ` by ${result.last_modified_by}` : ''}
      </p>
    )}
  </article>
);

const SetupState: React.FC<{ status: KbStatusResponse | null; isAdmin: boolean }> = ({ status, isAdmin }) => {
  const run = status?.latest_run;
  const notConfigured = run?.status === 'skipped' && run.details?.reason === 'kb_not_configured';
  const missing = (run?.details?.missing as string[] | undefined) ?? [];
  return (
    <section data-testid="kb-setup" style={{ ...card, padding: '1.5rem 1.25rem', textAlign: 'center', lineHeight: 1.55 }}>
      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>
        {notConfigured ? "The Knowledgebase isn't connected to Google Drive yet" : 'No documents indexed yet'}
      </h2>
      <p style={muted}>
        The Knowledgebase searches the team's SOPs and documents in the DWA Google shared drive. Drive stays
        where they're written and edited; this page finds them.
      </p>
      {notConfigured && isAdmin && (
        <p style={{ ...muted, marginTop: '0.6rem' }}>
          Missing configuration: {missing.length ? missing.map((m, i) => <React.Fragment key={m}>{i > 0 && ', '}<code>{m}</code></React.Fragment>) : <code>kb-drive-id</code>}.
          {' '}Add the service account key and shared drive id to Key Vault (setup steps in the Knowledge Bank entry
          <code> knowledgebase.md</code>), then click <strong>Sync now</strong>.
        </p>
      )}
      {!notConfigured && isAdmin && (
        <p style={{ ...muted, marginTop: '0.6rem' }}>
          {run?.status === 'running' ? 'A sync is running now.' : <>Click <strong>Sync now</strong> to index the shared drive (read-only).</>}
        </p>
      )}
      {!isAdmin && <p style={{ ...muted, marginTop: '0.6rem' }}>Ask a DWA admin to connect and sync it.</p>}
    </section>
  );
};

/** `active`: the selected folder path, '' for All folders, null for none (another Browse view is open). */
const FolderList: React.FC<{ folders: KbFolder[]; rootCount: number; active: string | null; onPick: (path: string) => void }> = ({
  folders,
  rootCount,
  active,
  onPick,
}) => {
  const [open, setOpen] = useState<Set<string>>(() => initialOpenFolders(folders, active ?? ''));
  // Folders arrive after the first render; open the top level (and the selected
  // folder's ancestors) once they do, without undoing the user's own toggles.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || folders.length === 0) return;
    seeded.current = true;
    setOpen(initialOpenFolders(folders, active ?? ''));
  }, [folders, active]);
  const toggle = (path: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  const rows = visibleFolderRows(folders, open);
  return (
    <Panel icon={faFolder} title="Folders">
      {folders.length === 0 && rootCount === 0 && <p style={muted}>No folders yet.</p>}
      <div role="tree" aria-label="Folders" style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', maxHeight: '28rem', overflowY: 'auto' }}>
        {(folders.length > 0 || rootCount > 0) && (
          <FolderButton label="All folders" depth={0} count={folders.reduce((n, f) => n + (f.depth === 0 ? f.count : 0), rootCount)} active={active === ''} onClick={() => onPick('')} />
        )}
        {rows.map(({ folder: f, hasChildren, open: isOpen }) => (
          <FolderButton
            key={f.path}
            label={f.name}
            title={f.path}
            depth={f.depth + 1}
            count={f.count}
            active={active === f.path}
            onClick={() => onPick(f.path)}
            expandable={hasChildren}
            expanded={isOpen}
            onToggle={() => toggle(f.path)}
          />
        ))}
      </div>
    </Panel>
  );
};

const FolderButton: React.FC<{
  label: string;
  title?: string;
  depth: number;
  count: number;
  active: boolean;
  onClick: () => void;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}> = ({ label, title, depth, count, active, onClick, expandable, expanded, onToggle }) => (
  <div
    role="treeitem"
    aria-expanded={expandable ? expanded : undefined}
    aria-selected={active}
    style={{
      display: 'flex',
      alignItems: 'center',
      borderRadius: DWA.radius,
      paddingLeft: `${0.2 + Math.min(depth, 6) * 0.85}rem`,
      background: active ? DWA.goldSoft : 'transparent',
    }}
  >
    {expandable ? (
      <button
        type="button"
        onClick={onToggle}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
        aria-expanded={expanded}
        style={{ width: '1.4rem', height: '1.6rem', flexShrink: 0, border: 'none', background: 'none', cursor: 'pointer', color: DWA.textSecondary, padding: 0 }}
      >
        <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} style={{ fontSize: '0.65rem' }} />
      </button>
    ) : (
      <span aria-hidden="true" style={{ width: '1.4rem', flexShrink: 0 }} />
    )}
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        justifyContent: 'space-between',
        gap: '0.5rem',
        alignItems: 'center',
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        background: 'transparent',
        padding: '0.4rem 0.55rem 0.4rem 0.1rem',
        color: DWA.ink,
        fontWeight: active ? 700 : 400,
        fontSize: '0.85rem',
        fontFamily: DWA.font,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{label}</span>
      <span style={{ color: DWA.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>{count}</span>
    </button>
  </div>
);

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; data: KbSearchResponse };

/** The Knowledgebase search UI (no layout), so tests can render it without the Hub shell. */
export const KnowledgebaseSearch: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'dwa_admin';
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const folder = params.get('folder') ?? '';
  const typeParam = params.get('type');
  const type = KB_TYPES.includes(typeParam as KbType) ? (typeParam as KbType) : null;
  const doc = params.get('doc') ?? '';
  // The Omega Wolf Guide opens in place of the SOP list (?guide=<page>).
  const guide = params.get('guide') ?? '';
  // Browse → Internal forms in the main column (?forms=all or ?forms=<form id>).
  const formsView = params.get('forms') ?? '';
  const [guideHits, setGuideHits] = useState<GuideHit[]>([]);

  const [prefs, setPrefsState] = useState<KbPrefs>(() => readKbPrefs(user?.id));
  const setPrefs = useCallback(
    (change: Partial<KbPrefs>) =>
      setPrefsState((cur) => {
        const next = { ...cur, ...change };
        writeKbPrefs(user?.id, next);
        return next;
      }),
    [user?.id],
  );
  const listView = !q && prefs.view === 'list';
  const [listPage, setListPage] = useState(1);
  // Back to page 1 whenever what's listed changes.
  useEffect(() => setListPage(1), [folder, type, prefs.listSort.sort, prefs.listSort.order, listView]);

  const [input, setInput] = useState(q);
  const [status, setStatus] = useState<KbStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [folders, setFolders] = useState<{ folders: KbFolder[]; root: number }>({ folders: [], root: 0 });
  const [state, setState] = useState<LoadState>({ kind: 'idle' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const requestId = useRef(0);

  // Keep the box in step when the URL changes (back/forward, folder clicks).
  useEffect(() => setInput((cur) => (cur.trim() === q ? cur : q)), [q]);

  const update = useCallback(
    (next: { q?: string; folder?: string; type?: KbType | null; doc?: string | null; guide?: string | null; forms?: string | null }) => {
      const p = new URLSearchParams(params);
      const set = (k: string, v: string | null | undefined) => (v ? p.set(k, v) : p.delete(k));
      if ('q' in next) set('q', next.q?.trim());
      if ('folder' in next) set('folder', next.folder);
      if ('type' in next) set('type', next.type ?? null);
      if ('doc' in next) set('doc', next.doc ?? null);
      if ('guide' in next) set('guide', next.guide ?? null);
      if ('forms' in next) set('forms', next.forms ?? null);
      // Opening a document or Guide page adds a history entry, so Back returns.
      setParams(p, { replace: !(('doc' in next && next.doc) || ('guide' in next && next.guide)) });
    },
    [params, setParams],
  );

  // Guide pages matching the search, shown above the SOP results.
  useEffect(() => {
    if (!q) {
      setGuideHits([]);
      return;
    }
    let cancelled = false;
    searchGuide(q)
      .then((r) => !cancelled && setGuideHits(r.results))
      .catch(() => !cancelled && setGuideHits([]));
    return () => {
      cancelled = true;
    };
  }, [q]);

  // Debounced search-as-you-type; Enter / the button searches at once.
  useEffect(() => {
    if (input.trim() === q) return;
    const t = setTimeout(() => update({ q: input }), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input, q, update]);

  const loadMeta = useCallback(() => {
    fetchKbStatus()
      .then((s) => {
        setStatus(s);
        setStatusError(null);
      })
      .catch((e: Error) => setStatusError(e.message));
    fetchKbFolders()
      .then((f) => setFolders({ folders: f.folders, root: f.root_count }))
      .catch(() => setFolders({ folders: [], root: 0 }));
  }, []);

  useEffect(loadMeta, [loadMeta]);

  const searchSort: KbSearchSort = prefs.searchSort;
  const { sort: listSort, order: listOrder } = prefs.listSort;
  useEffect(() => {
    const id = ++requestId.current;
    setState({ kind: 'loading' });
    const request = q
      ? searchKnowledgebase({ q, folder: folder || null, type, limit: PAGE_SIZE, ...(searchSort !== 'relevance' ? { sort: searchSort } : {}) })
      : listView
        ? searchKnowledgebase({ q: '', folder: folder || null, type, sort: listSort, order: listOrder, page: listPage, pageSize: LIST_PAGE_SIZE })
        : searchKnowledgebase({ q, folder: folder || null, type, limit: PAGE_SIZE });
    request
      .then((data) => {
        if (id === requestId.current) setState({ kind: 'loaded', data });
      })
      .catch((e: Error) => {
        if (id === requestId.current) setState({ kind: 'error', message: e.message });
      });
  }, [q, folder, type, searchSort, listView, listSort, listOrder, listPage]);

  const openDoc = useCallback((fileId: string) => update({ doc: fileId }), [update]);
  const closeDoc = useCallback(() => update({ doc: null }), [update]);

  const loadMore = () => {
    if (state.kind !== 'loaded') return;
    const current = state.data;
    setLoadingMore(true);
    searchKnowledgebase({
      q,
      folder: folder || null,
      type,
      limit: PAGE_SIZE,
      offset: current.results.length,
      ...(q && searchSort !== 'relevance' ? { sort: searchSort } : {}),
    })
      .then((more) => setState({ kind: 'loaded', data: { ...more, results: [...current.results, ...more.results] } }))
      .catch((e: Error) => setState({ kind: 'error', message: e.message }))
      .finally(() => setLoadingMore(false));
  };

  const sync = () => {
    setSyncMsg('Starting sync…');
    syncKnowledgebase()
      .then(() => {
        setSyncMsg('Sync queued. It reads the shared drive (read-only); refresh in a minute or two.');
        setTimeout(loadMeta, 3000);
      })
      .catch((e: Error) => setSyncMsg(`Could not start a sync: ${e.message}`));
  };

  const last = status?.last_success;
  const latest = status?.latest_run;
  const hasDocs = (status?.document_count ?? 0) > 0;
  const showSetup = status !== null && !hasDocs;
  const data = state.kind === 'loaded' ? state.data : null;

  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '1.75rem clamp(1rem, 4vw, 2rem) 6rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, margin: '0 0 0.35rem', color: '#ffffff' }}>Knowledgebase</h1>
          <p style={{ margin: 0, color: DWA.onDarkMuted, fontSize: '0.95rem' }}>
            The team's SOPs from the DWA shared drive, our internal request forms, and the Omega Wolf Guide, all in one search. SOPs open in Google Drive for editing.
          </p>
          <p data-testid="kb-last-synced" style={{ margin: '0.4rem 0 0', color: DWA.onDarkMuted, fontSize: '0.8rem' }}>
            {statusError
              ? 'Sync status unavailable.'
              : status === null
                ? ' '
                : last
                  ? `Last synced ${formatKbDate(last.finished_at ?? last.started_at, true)} · ${status.document_count} document${status.document_count === 1 ? '' : 's'}`
                  : 'Not synced yet.'}
            {latest?.status === 'running' && ' · sync running'}
            {latest?.status === 'failed' && <span style={{ color: '#fca5a5' }}> · last sync failed</span>}
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Link
              to="/knowledgebase-sources"
              data-testid="kb-manage-sources"
              style={{ ...goldButton, background: 'transparent', color: DWA.gold, border: `1px solid ${DWA.gold}`, textDecoration: 'none' }}
            >
              Manage sources
            </Link>
            <button type="button" onClick={sync} className="hub-gold-button" style={goldButton}>
              Sync now
            </button>
          </div>
        )}
      </div>
      {syncMsg && <p style={{ margin: '0.6rem 0 0', color: DWA.onDarkMuted, fontSize: '0.8rem' }}>{syncMsg}</p>}

      {guide ? (
        <GuideView slug={guide} onOpen={(slug) => update({ guide: slug })} onClose={() => update({ guide: null })} />
      ) : (
      <>

      {showSetup ? (
        <div style={{ marginTop: '1.5rem' }}>
          <SetupState status={status} isAdmin={isAdmin} />
        </div>
      ) : (
        <>
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: input });
            }}
            style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
          >
            <label htmlFor="kb-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
              Search the Knowledgebase
            </label>
            <div style={{ position: 'relative', flex: '1 1 18rem', minWidth: 0 }}>
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: DWA.textMuted }}
              />
              <input
                id="kb-search"
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Search SOPs, forms and the guide, e.g. website launch checklist"
                autoComplete="off"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '0.9rem 1rem 0.9rem 2.6rem',
                  borderRadius: DWA.radius,
                  border: 'none',
                  fontSize: '1rem',
                  fontFamily: DWA.font,
                  background: DWA.cardBg,
                  color: DWA.ink,
                  boxShadow: DWA.cardShadow,
                }}
              />
            </div>
            <button type="submit" className="hub-gold-button" style={{ ...goldButton, padding: '0.9rem 1.3rem', fontSize: '0.95rem' }}>
              Search
            </button>
          </form>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem', alignItems: 'center' }}>
            <select aria-label="Folder" value={folder} onChange={(e) => update({ folder: e.target.value })} style={{ ...select, flex: '1 1 12rem' }}>
              <option value="">All folders</option>
              {folder && !folders.folders.some((f) => f.path === folder) && <option value={folder}>{folder}</option>}
              {folders.folders.map((f) => (
                <option key={f.path} value={f.path}>
                  {`${'  '.repeat(f.depth)}${f.name} (${f.count})`}
                </option>
              ))}
            </select>
            <select aria-label="Type" value={type ?? ''} onChange={(e) => update({ type: (e.target.value || null) as KbType | null })} style={{ ...select, flex: '0 1 10rem' }}>
              <option value="">All types</option>
              {KB_TYPES.map((t) => (
                <option key={t} value={t}>
                  {KB_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            {(folder || type) && (
              <button
                type="button"
                onClick={() => update({ folder: '', type: null })}
                style={{ border: 'none', background: 'none', color: DWA.gold, cursor: 'pointer', fontSize: '0.8rem', fontFamily: DWA.font }}
              >
                Clear filters
              </button>
            )}
          </div>

          {state.kind === 'error' && (
            <div role="alert" style={{ ...card, marginTop: '1.25rem', padding: '1rem 1.15rem', color: DWA.danger, fontSize: '0.88rem' }}>
              Couldn't load the Knowledgebase: {state.message}
            </div>
          )}

          {q ? (
            <>
            <GuideHits hits={guideHits} onOpen={(slug) => update({ guide: slug })} />
            <InternalFormsPanel query={q} />
            <section aria-label="Search results" style={{ marginTop: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.75rem' }}>
                <p data-testid="kb-result-count" style={{ margin: 0, color: DWA.onDarkMuted, fontSize: '0.85rem' }}>
                  {state.kind === 'loading' && 'Searching…'}
                  {data && `${data.total} result${data.total === 1 ? '' : 's'} for “${data.query}”${folder ? ` in ${folder}` : ''}`}
                </p>
                <select
                  aria-label="Sort results"
                  value={searchSort}
                  onChange={(e) => setPrefs({ searchSort: e.target.value as KbSearchSort })}
                  style={{ ...select, flex: '0 1 11rem' }}
                >
                  {SEARCH_SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {data && data.results.length === 0 && (
                <div data-testid="kb-no-results" style={{ ...card, padding: '1.25rem', textAlign: 'center' }}>
                  <h2 style={{ margin: '0 0 0.35rem', fontSize: '1rem' }}>No documents match</h2>
                  <p style={muted}>
                    Try fewer or different words{folder || type ? ', or clear the filters' : ''}. Use quotes for an exact phrase and
                    a minus sign to exclude a word.
                  </p>
                </div>
              )}
              <ResultsList data={data} onFolder={(p) => update({ folder: p })} onOpen={openDoc} onMore={loadMore} loadingMore={loadingMore} />
            </section>
            </>
          ) : listView ? (
            <section aria-label="All documents" style={{ marginTop: '1.25rem' }}>
              <ViewToggle view="list" onChange={(view) => setPrefs({ view })} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', margin: '0.75rem 0' }}>
                <p data-testid="kb-list-count" style={{ margin: 0, color: DWA.onDarkMuted, fontSize: '0.85rem' }}>
                  {state.kind === 'loading' && 'Loading…'}
                  {data && `${data.total} document${data.total === 1 ? '' : 's'}${folder ? ` in ${folder}` : ''}`}
                </p>
                <select
                  aria-label="Sort documents"
                  value={`${listSort}:${listOrder}`}
                  onChange={(e) => {
                    const next = parseListSort(e.target.value);
                    if (next) setPrefs({ listSort: next });
                  }}
                  style={{ ...select, flex: '0 1 13rem' }}
                >
                  {LIST_SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {data && <DocumentTable data={data} onOpen={openDoc} onFolder={(p) => update({ folder: p })} />}
              {data && data.total > LIST_PAGE_SIZE && (
                <Pager page={listPage} pages={Math.ceil(data.total / LIST_PAGE_SIZE)} onPage={setListPage} />
              )}
            </section>
          ) : (
            <>
            <div style={{ marginTop: '1.25rem' }}>
              <ViewToggle view="overview" onChange={(view) => setPrefs({ view })} />
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
              <div aria-label="Browse" style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <FolderList folders={folders.folders} rootCount={folders.root} active={formsView ? null : folder} onPick={(p) => update({ folder: p, forms: null })} />
                <FormsSidebar active={!!formsView} selected={formsView} onOpen={(id) => update({ forms: id ?? 'all' })} />
                <GuideSidebar onOpen={() => update({ guide: GUIDE_START })} />
              </div>
              {formsView ? (
                <div style={{ flex: '2 1 420px', minWidth: 0 }}>
                  <InternalFormsPanel highlight={formsView} />
                </div>
              ) : (
              <section aria-label="Recently updated" style={{ flex: '2 1 420px', minWidth: 0 }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.75rem', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: DWA.gold }}>
                  <FontAwesomeIcon icon={faClock} />
                  Recently updated{folder ? ` in ${folder}` : ''}
                </h2>
                {state.kind === 'loading' && <p style={{ color: DWA.onDarkMuted, fontSize: '0.85rem' }}>Loading…</p>}
                {data && data.results.length === 0 && <p style={{ color: DWA.onDarkMuted, fontSize: '0.85rem' }}>Nothing here yet.</p>}
                <ResultsList data={data} onFolder={(p) => update({ folder: p })} onOpen={openDoc} onMore={loadMore} loadingMore={loadingMore} />
              </section>
              )}
            </div>
            </>
          )}
        </>
      )}
      </>
      )}
      {doc && <KbViewer fileId={doc} onClose={closeDoc} />}
    </main>
  );
};

const ViewToggle: React.FC<{ view: KbPrefs['view']; onChange: (view: KbPrefs['view']) => void }> = ({ view, onChange }) => {
  const option = (id: KbPrefs['view'], label: string, icon: typeof faList) => (
    <button
      type="button"
      aria-pressed={view === id}
      onClick={() => onChange(id)}
      style={{
        ...goldButton,
        padding: '0.4rem 0.8rem',
        fontSize: '0.8rem',
        background: view === id ? DWA.gold : 'transparent',
        color: view === id ? DWA.onGold : DWA.onDark,
        border: `1px solid ${view === id ? DWA.gold : 'rgba(255,255,255,0.25)'}`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
      }}
    >
      <FontAwesomeIcon icon={icon} />
      {label}
    </button>
  );
  return (
    <div role="group" aria-label="View" style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
      {option('overview', 'Overview', faTableCellsLarge)}
      {option('list', 'Browse all', faList)}
    </div>
  );
};

const cell: React.CSSProperties = { padding: '0.55rem 0.7rem', textAlign: 'left', verticalAlign: 'top', fontSize: '0.85rem' };

const DocumentTable: React.FC<{ data: KbSearchResponse; onOpen: (fileId: string) => void; onFolder: (path: string) => void }> = ({
  data,
  onOpen,
  onFolder,
}) => {
  if (data.results.length === 0) return <p style={{ color: DWA.onDarkMuted, fontSize: '0.85rem' }}>Nothing here yet.</p>;
  return (
    <div style={{ ...card, overflowX: 'auto' }}>
      <table data-testid="kb-doc-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 0 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${DWA.cardBorder}`, color: DWA.textSecondary }}>
            <th style={cell}>Title</th>
            <th style={{ ...cell, width: '30%' }}>Folder</th>
            <th style={cell}>Type</th>
            <th style={{ ...cell, whiteSpace: 'nowrap' }}>Updated</th>
          </tr>
        </thead>
        <tbody>
          {data.results.map((r) => (
            <tr key={r.file_id} data-testid={`kb-row-${r.file_id}`} style={{ borderBottom: `1px solid ${DWA.cardBorder}` }}>
              <td style={{ ...cell, fontWeight: 700, minWidth: '10rem' }}>
                <button type="button" onClick={() => onOpen(r.file_id)} style={titleButton}>
                  {r.title}
                </button>
              </td>
              <td style={{ ...cell, color: DWA.textSecondary, minWidth: '8rem' }}>
                {r.folder_path ? (
                  <button type="button" onClick={() => onFolder(r.folder_path)} style={{ ...titleButton, color: DWA.textSecondary, fontWeight: 400 }}>
                    {r.folder_path}
                  </button>
                ) : (
                  'Top level'
                )}
              </td>
              <td style={{ ...cell, color: DWA.textSecondary }}>{r.kind_label}</td>
              <td style={{ ...cell, color: DWA.textSecondary, whiteSpace: 'nowrap' }}>{formatKbDate(r.modified_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Pager: React.FC<{ page: number; pages: number; onPage: (page: number) => void }> = ({ page, pages, onPage }) => (
  <nav aria-label="Pages" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '0.9rem', color: DWA.onDarkMuted, fontSize: '0.85rem' }}>
    <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} style={{ ...goldButton, padding: '0.4rem 0.8rem', opacity: page <= 1 ? 0.4 : 1 }}>
      Previous
    </button>
    <span data-testid="kb-page">
      Page {page} of {pages}
    </span>
    <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} style={{ ...goldButton, padding: '0.4rem 0.8rem', opacity: page >= pages ? 0.4 : 1 }}>
      Next
    </button>
  </nav>
);

const ResultsList: React.FC<{
  data: KbSearchResponse | null;
  onFolder: (path: string) => void;
  onOpen: (fileId: string) => void;
  onMore: () => void;
  loadingMore: boolean;
}> = ({ data, onFolder, onOpen, onMore, loadingMore }) => {
  if (!data || data.results.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {data.results.map((r) => (
        <ResultCard key={r.file_id} result={r} onFolder={onFolder} onOpen={onOpen} />
      ))}
      {data.results.length < data.total && (
        <button
          type="button"
          onClick={onMore}
          disabled={loadingMore}
          style={{ ...goldButton, alignSelf: 'center', background: 'transparent', color: DWA.gold, border: `1px solid ${DWA.gold}` }}
        >
          {loadingMore ? 'Loading…' : `Show more (${data.total - data.results.length} more)`}
        </button>
      )}
    </div>
  );
};

export const KnowledgebasePage: React.FC = () => (
  <HubLayout>
    <GradientPage>
      <KnowledgebaseSearch />
    </GradientPage>
  </HubLayout>
);

export default KnowledgebasePage;
